import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError, firstValueFrom } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { IInvoiceInfo, ApiService, WebStorageService, STORAGE_SESSION_KEY, SPECIAL_USER_LOGIN_TYPES } from '@cakeiteasy/web-utils';

import { UserState } from '../store/reducers/user.reducer';
import { LoadUserSuccess, LoadUserFail, LogUserOut, UpdateUserInfo, UpdateUserInvoiceInfo } from '../store/user.actions';
import { IUser } from '../models/i-user';
import { IDeliveryAddress } from '../models/i-delivery-address';
import { UserApiService } from '../../api-services/services/user-api.service';
import { PaymentStorageService } from '../../payment/services/payment-storage.service';

@Injectable()
export class UserManager {
  public userHaveBeenLogin$ = new BehaviorSubject<boolean>(false);

  constructor(private _store: Store<UserState>,
              private _apiService: ApiService,
              private _users: UserApiService,
              private _paymentStorage: PaymentStorageService,
              private _storage: WebStorageService) {
  }

  public getLoginType(params: { email: string, bakeryId: number }): Promise<{ loginType: SPECIAL_USER_LOGIN_TYPES }> {
    return firstValueFrom(this._users.getLoginType(params));
  }

  public makeLoginActions(): Promise<any> {
    this._paymentStorage.clearStorage();
    return this.resetSessionId();
  }

  /**Original login is affected loading cart after dispatchUser, in some cases login should be without cart fetch
   * and directly login and dispatchUser should be in separated flow*/
  public async pureLogin(credentials): Promise<IUser> {
    const user = await this._users.login(credentials);
    await this.makeLoginActions();
    return user;
  }

  public login(credentials) {
    let user;
    return this._users
      .login(credentials)
      .then((resUser) => user = resUser)
      // we don't remove payment-type as when user loggs in we have all the info about him
      .then(() => this._paymentStorage.clearStorage())
      .then(() => this.resetSessionId())
      .then(() => {
        this.userHaveBeenLogin$.next(true);
        this.dispatchUser(user);
        return user;
      });
  }

  public register(user) {
    let credentials = { email: user.email, password: user.password } as any;

    if (user.bakery) {
      credentials = { ...credentials, bakery: user.bakery };
    }
    return this._users
      .register(user)
      .then(() => this.login(credentials));
  }

  public logout() {
    return this._users
      .logout()
      .then(() => this._paymentStorage.clearStorage())
      .then(() => this.resetSessionId())
      .then(() => this._store.dispatch(new LogUserOut()));
  }

  public fetchCurrentUser(): Observable<IUser> {
    return this._users.fetchCurrentUser()
      .pipe(
        tap((user) => this.dispatchUser(user)),
        catchError((error) => {
          this._store.dispatch(new LoadUserFail(error));
          return throwError(error);
        })
      );
  }

  public saveInvoiceInfo(invoiceInfo: IInvoiceInfo): Observable<IInvoiceInfo> {
    return this._users.saveInvoiceInfo(invoiceInfo)
      .pipe(tap((resInvoiceInfo) => {
        this._store.dispatch(new UpdateUserInvoiceInfo(resInvoiceInfo));
      }));
  }

  public removeInvoiceInfo(): Promise<void> {
    return firstValueFrom(this._users.removeInvoiceInfo())
      .then(() => {
        this._store.dispatch(new UpdateUserInvoiceInfo(null));
      });
  }

  public updateInfo(userInfo: IUser) {
    return firstValueFrom(this._users.updateInfo(userInfo))
      .then((user) => {
        this._store.dispatch(new UpdateUserInfo(user));
      });
  }

  public resetPassword(data) {
    return firstValueFrom(this._users.resetPassword(data));
  }

  public restorePassword(data) {
    return firstValueFrom(this._users.restorePassword(data));
  }

  public restorePasswordRequest(data) {
    return firstValueFrom(this._users.restorePasswordRequest(data));
  }

  public saveDeliveryAddress(deliveryAddress: IDeliveryAddress) {
    if (deliveryAddress && deliveryAddress.id) {
      return this._users.updateDeliveryAddress(deliveryAddress);
    }
    return this._users.addDeliveryAddress(deliveryAddress);
  }

  public resetSessionId() {
    this._storage.session.remove(STORAGE_SESSION_KEY);

    return firstValueFrom(this._apiService.get('api.session', 'current', null))
      .then((res) => this._storage.session.save(STORAGE_SESSION_KEY, res.key));
  }

  public dispatchUser(user: IUser): void {
    this._store.dispatch(new LoadUserSuccess(user));
  }
}
